import { useState, useEffect, useRef } from 'react'

import {MC} from './MC.js'

const useAudioLevels = (isRecording) => {
  const [audioLevel, setAudioLevel] = useState(0)

  useEffect(() => {
    let audioContext
    let analyser
    let source
    if (isRecording) {
      const handleSuccess = (stream) => {
        audioContext = new (window.AudioContext || window.webkitAudioContext)()
        analyser = audioContext.createAnalyser()
        source = audioContext.createMediaStreamSource(stream)
        analyser.smoothingTimeConstant = 0.8
        analyser.fftSize = 1024
        source.connect(analyser)
        const getAudioLevel = () => {
          const dataArray = new Uint8Array(analyser.frequencyBinCount)
          analyser.getByteFrequencyData(dataArray)

          let sum = 0
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i]
          }
          const average = sum / dataArray.length
          setAudioLevel(average)
          requestAnimationFrame(getAudioLevel)
        }
        getAudioLevel()
      }
      navigator.mediaDevices.getUserMedia({ audio: true }).then(handleSuccess)
      return () => {
        if (source) source.disconnect()
        if (analyser) analyser.disconnect()
        if (audioContext) audioContext.close()
      }
    }
  }, [isRecording])

  return audioLevel
}

const Microphone = (props) => {
  const data = props.data
  const [time, setTime] = useState(0)
  const timerRef = useRef()
  const mediaRecorderRef = useRef()
  const [currentState, setCurrentState] = useState('idle')
  const audioLevel = useAudioLevels(MC.getFieldParamValue(data.param, '@state') === 'recording')

  const startInterval = () => {
    timerRef.current = window.setInterval(() => {
      setTime(prev => {
        const nt = prev + 1
        MC.putFieldParamValue(data.param, '@timeElapsed', nt)
        MC.handleEvent(data, 'change')
        return nt
      })
    }, 1000)
  }

   // Function to start recording
  const startRecording = async () => {
    setTime(0)
    startInterval()
    let mimeType = MC.getFieldParamValue(data.param, '@mimeType')
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm'
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4' 
      } else {
        mimeType = 'audio/mpeg'
      }
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    mediaRecorderRef.current = new MediaRecorder(stream, { mimeType })
    const chunks = []
    mediaRecorderRef.current.ondataavailable = (e) => chunks.push(e.data)
    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(chunks, { type: mediaRecorderRef.current.mimeType })
      MC.blobToBase64(blob).then((b64) => {
        MC.putFieldParamValue(data.param, 'value', b64.split(',')[1]) // Remove the data:...;base64
        MC.putFieldParamValue(data.param, '@mimeType', mediaRecorderRef.current.mimeType)
        MC.handleEvent(data, 'changecommitted')
      })
    }
    mediaRecorderRef.current.start()
  }

  React.useEffect(() => {
    let state = MC.getFieldParamValue(data.param, '@state')
    if (state != currentState) {
      if (state === 'recording') {
        if (currentState === 'paused') {
          startInterval()
          if (mediaRecorderRef.current) mediaRecorderRef.current.resume()
        } else if (currentState === 'stopped') {
          setTime(0)
          MC.putFieldParamValue(data.param, '@timeElapsed', 0)
          MC.putFieldParamValue(data.param, 'value', null)
          MC.putFieldParamValue(data.param, '@mimeType', null)
          startRecording()
        } else {
          startRecording()
        }  
        setCurrentState('recording')
      } else if (state === 'paused') {
        setCurrentState('paused')
        clearInterval(timerRef.current)
        if (mediaRecorderRef.current) mediaRecorderRef.current.pause()
      } else if (state === 'stopped') {
        setCurrentState('stopped')
        clearInterval(timerRef.current)
        if (mediaRecorderRef.current) mediaRecorderRef.current.stop()
      }
    }
  }, [props])

  React.useEffect(() => {
    MC.putFieldParamValue(data.param, '@timeElapsed', 0) // Initialize timeElapsed param
    return () => { // Cleanup on unmount
      clearInterval(timerRef.current)
    }
  }, [])

  const icon = MC.getFieldParamValue(data.param, '@icon') || 'microphone'
  return (
    <div data-widget-i-name={props.data.id} className='border relative flex flex-col justify-center items-center h-72 w-full shadow-lg rounded-md'>
      <div className="hbox flex-row widget-hbox">
        <div className="gapped">
          <div style={{backgroundColor: 'rgb(249 250 251)', borderRadius: '9999px', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '4em', height: '4em'}}>
            <div style={{transform: `scale(${audioLevel / 20})`, backgroundColor: 'rgb(209 213 219)', borderRadius: '9999px', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '1.5em', height: '1.5em'}}>
              <div style={{backgroundColor: 'rgb(156 163 175)', borderRadius: '9999px', width: '.5em', height: '.5em'}}></div>
            </div>
            <i className={MC.buildIconClass(icon)} style={{fontSize: '1.5em', position: 'absolute', marginRight: '0px'}}/>
          </div>
        </div>
      </div>
      <label className={props.helpCls} key="error">{props.help}</label>
    </div>
  )
}

export default Microphone